import React from 'react';

import {ProcessFlowchartNodeDetailsType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {ProcessFlowchartNodeType} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/processFlowchartConfigEditorConstants';
import {
  FlowchartNode,
  isInteractiveRouterNode,
} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/processFlowchartConfigEditorNodeTypes';
import RouterNodeDetailsSidepanelContent from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/right-sidepanel/RouterNodeDetailsSidepanelContent';

import {DefaultProcessFlowchartNodeAdapter} from '../flowchart-viz/horizontal-flowchart/nodes/ProcessFlowchartNodeAdapter';
import doesFlowchartHaveRouterWithAndOrFunction from '../flowchart-viz/util/doesFlowchartHaveRouterWithAndOrFunction';
import {
  ProcessFlowchartForValidation,
  ProcessFlowchartValidationError,
  ValidationContext,
} from '../validation/ProcessFlowchartForValidationTypes';
import {pushErrors} from '../validation/validateProcessFlowchartHelpers';
import {
  ensureLoopsContainTaskAndRouterNode,
  ensureLoopsHaveOneEntryPoint,
} from '../validation/validateProcessFlowchartRules';
import {
  ensureChainedRouterLimit,
  ensureNoChainedRouters,
  ensureNoMultipleRouterOnlyPathsBetweenTaskNodes,
  ensureRouterNodeConnectedToNonEndNode,
  ensureRouterNodeDestinationNodesAreNotSamePTDV,
  ensureRouterNodeHasOneIncomingEdge,
  ensureRouterNodeHasOneSourceSchema,
  ensureRouterNodeHasRouteConfiguredForEachEdge,
  ensureRouterNodeHasTwoOutgoingEdges,
} from '../validation/validateProcessRouterNodes';

/**
 * Adapter allowing stop nodes to be rendered in the processes editor.
 */
export class StopNodeAdapter extends DefaultProcessFlowchartNodeAdapter {
  nodeType: ProcessFlowchartNodeType = ProcessFlowchartNodeType.STOP_NODE;

  validateFlowchartForNodeType(
    flowchart: ProcessFlowchartForValidation,
    validationContext: ValidationContext
  ): Array<ProcessFlowchartValidationError> {
    let validationErrors: Array<ProcessFlowchartValidationError> = [];
    for (const node of flowchart.nodes) {
      if (node.nodeType === ProcessFlowchartNodeDetailsType.ROUTER_NODE_DETAILS) {
        validationErrors = pushErrors(
          validationErrors,
          ensureRouterNodeConnectedToNonEndNode(node, flowchart)
        );
      }
    }
    return validationErrors;
  }

  override maxIncomingEdges(isFlowchartOutput?: boolean): number {
    return 1;
  }
  override maxOutgoingEdges(isFlowchartOutput?: boolean): number {
    return 0;
  }
  override blockedAdjacentNodeTypes(): Array<ProcessFlowchartNodeType> {
    return [ProcessFlowchartNodeType.STOP_NODE, ProcessFlowchartNodeType.TASK_SCHEMA_NODE];
  }
}

/**
 * Adapter allowing router nodes to be rendered in the processes editor.
 */
export class RouterNodeAdapter extends DefaultProcessFlowchartNodeAdapter {
  nodeType: ProcessFlowchartNodeType = ProcessFlowchartNodeType.ROUTER_NODE;

  validateFlowchartForNodeType(
    flowchart: ProcessFlowchartForValidation,
    validationContext: ValidationContext
  ): Array<ProcessFlowchartValidationError> {
    const {designSpace, didInitialFlowchartHaveChainedRouters, disconnectedNodeIds} = validationContext;
    let validationErrors: Array<ProcessFlowchartValidationError> = [];
    for (const node of flowchart.nodes) {
      if (node.nodeType === ProcessFlowchartNodeDetailsType.ROUTER_NODE_DETAILS) {
        if (!disconnectedNodeIds.includes(node.id)) {
          validationErrors = pushErrors(
            validationErrors,
            ensureRouterNodeHasOneIncomingEdge(node, flowchart.edges)
          );
          validationErrors = pushErrors(
            validationErrors,
            ensureRouterNodeHasTwoOutgoingEdges(node, flowchart.edges)
          );
          validationErrors = pushErrors(validationErrors, ensureChainedRouterLimit(node, flowchart));
        }
        if (
          !didInitialFlowchartHaveChainedRouters ||
          (didInitialFlowchartHaveChainedRouters && doesFlowchartHaveRouterWithAndOrFunction(flowchart))
        ) {
          validationErrors = pushErrors(validationErrors, ensureNoChainedRouters(node, flowchart));
        }
        validationErrors = pushErrors(
          validationErrors,
          ensureRouterNodeDestinationNodesAreNotSamePTDV({node, flowchart, usingFlowchartFirstConfig: true})
        );
        validationErrors = pushErrors(
          validationErrors,
          ensureRouterNodeHasRouteConfiguredForEachEdge(node, flowchart)
        );
        validationErrors = pushErrors(
          validationErrors,
          ensureRouterNodeHasOneSourceSchema({
            node,
            flowchart,
            designSpace,
            usingFlowchartFirstConfig: true,
          })
        );
      }
      if (node.nodeType === ProcessFlowchartNodeDetailsType.TASK_NODE_DETAILS) {
        validationErrors = pushErrors(
          validationErrors,
          ensureNoMultipleRouterOnlyPathsBetweenTaskNodes({
            destinationNode: node,
            flowchart,
            usingFlowchartFirstConfig: true,
          })
        );
      }
      validationErrors = pushErrors(validationErrors, ensureLoopsContainTaskAndRouterNode(node, flowchart));
      validationErrors = pushErrors(validationErrors, ensureLoopsHaveOneEntryPoint(flowchart));
    }
    return validationErrors;
  }

  override maxIncomingEdges(isFlowchartOutput?: boolean): number {
    return 1;
  }

  override blockedAdjacentNodeTypes(): Array<ProcessFlowchartNodeType> {
    return super.blockedAdjacentNodeTypes();
  }

  override getSidepanelTitle(nodeName: string): string {
    return `Edit router: ${nodeName}`;
  }

  override nodeDetailsSidepanelContent(selectedNode: FlowchartNode): JSX.Element | null {
    if (!isInteractiveRouterNode(selectedNode)) {
      return null;
    }
    return <RouterNodeDetailsSidepanelContent selectedNode={selectedNode} />;
  }
}
